
import { useState, useEffect, useRef, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOptimizedAuth } from "@/hooks/useOptimizedAuth";
import { Search, X, Clock, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

interface SearchResult {
  id: string;
  name: string;
  price: number;
  image_url: string | null;
  url_slug: string | null;
}

const ModernSearchBar = forwardRef<HTMLInputElement>((_, ref) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const navigate = useNavigate();
  const { user } = useOptimizedAuth();
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user?.id) {
      const saved = localStorage.getItem(`recent-searches-${user.id}`);
      if (saved) setRecentSearches(JSON.parse(saved));
    }
  }, [user?.id]);

  const saveSearch = (term: string) => {
    if (!term.trim() || !user?.id) return;
    const updated = [term.trim(), ...recentSearches.filter(s => s.toLowerCase() !== term.trim().toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem(`recent-searches-${user.id}`, JSON.stringify(updated));
  };

  const handleSearchSubmit = (e: React.FormEvent, term?: string) => {
    e.preventDefault();
    const query = (term || searchQuery).trim();
    if (query) {
      saveSearch(query);
      setIsOpen(false);
      setSearchQuery(query);
      navigate(`/products?search=${encodeURIComponent(query)}`);
    }
  };
  
  const clearRecentSearches = () => {
    setRecentSearches([]);
    if (user?.id) localStorage.removeItem(`recent-searches-${user.id}`);
  };

  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['header_search', searchQuery],
    queryFn: async (): Promise<SearchResult[]> => {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, price, image_url, url_slug')
        .ilike('name', `%${searchQuery}%`)
        .eq('status', 'active')
        .limit(5);
      if (error) return [];
      return data as SearchResult[];
    },
    enabled: searchQuery.length > 2 && isOpen,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  
  const handleSuggestionClick = (product: SearchResult) => {
    setIsOpen(false);
    navigate(`/product/${product.url_slug}`);
  };

  const showSuggestions = isOpen && searchQuery.length > 2;
  const showRecent = isOpen && searchQuery.length <= 2 && recentSearches.length > 0;

  return (
    <div className="relative w-full" ref={searchContainerRef}>
      <form onSubmit={handleSearchSubmit} className="relative w-full">
        <div className="relative group">
          <div 
            className="absolute -inset-0.5 bg-gradient-to-r from-fuchsia-500 via-purple-500 to-blue-500 rounded-full blur opacity-0 group-focus-within:opacity-75 transition-opacity duration-300"
          />
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none group-focus-within:text-primary transition-colors" />
            <Input
              ref={ref}
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsOpen(true)}
              className="pl-12 pr-4 h-11 w-full bg-background/80 dark:bg-zinc-900/80 backdrop-blur-sm border-0 rounded-full focus:ring-0 focus:outline-none transition-all duration-200"
              autoComplete="off"
            />
          </div>
        </div>
      </form>
      <AnimatePresence>
        {(showSuggestions || showRecent) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-background/95 dark:bg-zinc-900/95 backdrop-blur-xl border border-border/80 rounded-xl shadow-2xl z-[9999] overflow-hidden"
          >
            {showRecent && (
              <div className="p-2">
                <div className="flex items-center justify-between px-2 py-1">
                  <h3 className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">Recent Searches</h3>
                  <Button variant="ghost" size="sm" onClick={clearRecentSearches} className="text-xs h-auto py-1 px-2">Clear</Button>
                </div>
                <div className="mt-1">
                  {recentSearches.map((search) => (
                    <button key={search} onClick={(e) => handleSearchSubmit(e, search)} className="w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-primary/10 transition-colors">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{search}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {showSuggestions && (
              <div className="p-2">
                {isSearching && <div className="p-4 flex justify-center items-center"><Loader2 className="h-5 w-5 animate-spin text-primary"/></div>}
                {!isSearching && searchResults && searchResults.length > 0 && (
                  <div>
                    {searchResults.map((product) => (
                      <button key={product.id} onClick={() => handleSuggestionClick(product)} className="w-full text-left flex items-center gap-3 p-2 rounded-lg hover:bg-primary/10 transition-colors">
                        <img src={product.image_url || '/placeholder.svg'} alt={product.name} className="h-10 w-10 object-cover rounded-md"/>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground">${product.price}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {!isSearching && (!searchResults || searchResults.length === 0) && (
                  <p className="p-4 text-center text-sm text-muted-foreground">No products found for "{searchQuery}"</p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

ModernSearchBar.displayName = "ModernSearchBar";

export default ModernSearchBar;
